import React from 'react';
import { Link } from 'react-router-dom';
import './SpxLanding.css';

export const SpxLanding: React.FC = () => {
  return (
    <div className="spxl-page">
      {/* ---- Nav ---- */}
      <nav className="spxl-nav">
        <Link to="/" className="spxl-nav-brand">
          <div className="spxl-nav-logo">SPX</div>
          <span className="spxl-nav-title">SPX Signals</span>
        </Link>
        <div className="spxl-nav-actions">
          <Link to="/" className="spxl-nav-link">Home</Link>
          <Link to="/login" className="spxl-btn spxl-btn-primary">Get Started</Link>
        </div>
      </nav>

      {/* ---- Hero ---- */}
      <section className="spxl-hero">
        <div className="spxl-hero-badge">AI-POWERED OPTIONS INTELLIGENCE</div>
        <h1>Find the Best SPX Put to Sell. Every Day.</h1>
        <p className="spxl-hero-subtitle">
          Our system analyzes hundreds of SPX weekly put options across every expiration,
          scores them with a proprietary quantitative model, then validates the top pick
          with two independent AI engines — so you trade with confidence, not guesswork.
        </p>
        <div style={{ margin: '1.5rem 0', display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '3rem', fontWeight: 800, color: '#8b5cf6' }}>$79</span>
          <span style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.5)' }}>/month</span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>60-day money-back guarantee. Cancel anytime.</p>
        <div className="spxl-hero-actions">
          <Link to="/login?redirect=upgrade" className="spxl-btn spxl-btn-primary spxl-btn-lg">
            Subscribe Now
          </Link>
          <a href="#how-it-works" className="spxl-btn spxl-btn-outline spxl-btn-lg">
            See How It Works
          </a>
        </div>
      </section>

      {/* ---- Stats Strip ---- */}
      <section className="spxl-stats">
        <div className="spxl-stat">
          <div className="spxl-stat-val">700+</div>
          <div className="spxl-stat-label">Put Options Analyzed</div>
        </div>
        <div className="spxl-stat">
          <div className="spxl-stat-val">3x</div>
          <div className="spxl-stat-label">Daily Snapshots</div>
        </div>
        <div className="spxl-stat">
          <div className="spxl-stat-val">3</div>
          <div className="spxl-stat-label">Independent Validations</div>
        </div>
        <div className="spxl-stat">
          <div className="spxl-stat-val">6</div>
          <div className="spxl-stat-label">Scoring Factors</div>
        </div>
      </section>

      {/* ---- How It Works ---- */}
      <section className="spxl-section" id="how-it-works">
        <div className="spxl-section-label">How It Works</div>
        <h2 className="spxl-section-title">From Raw Data to High-Confidence Trades</h2>
        <p className="spxl-section-desc">
          Three times every trading day, our system scans the entire SPX options market,
          scores hundreds of trades, and tells you which one looks best —
          all in under 15 minutes, completely automated.
        </p>

        <div className="spxl-pipeline">
          <div className="spxl-pipe-step">
            <div className="spxl-pipe-num">1</div>
            <div className="spxl-pipe-title">We Grab Live Prices</div>
            <div className="spxl-pipe-desc">
              Every SPX put option's real-time price, risk metrics, and market
              conditions — pulled straight from the brokerage, not delayed or
              estimated.
            </div>
          </div>
          <div className="spxl-pipe-step">
            <div className="spxl-pipe-num">2</div>
            <div className="spxl-pipe-title">We Score Every Trade</div>
            <div className="spxl-pipe-desc">
              Our model rates each option on: how much you earn, how safe it is,
              how likely you win, and how fast it pays. Trades are compared
              within the same time window so the ranking is fair.
            </div>
          </div>
          <div className="spxl-pipe-step">
            <div className="spxl-pipe-num">3</div>
            <div className="spxl-pipe-title">Two AIs Double-Check</div>
            <div className="spxl-pipe-desc">
              The top picks go to Claude (Anthropic) and ChatGPT (OpenAI)
              for a second and third opinion. Neither AI sees our rankings —
              they judge independently.
            </div>
          </div>
          <div className="spxl-pipe-step">
            <div className="spxl-pipe-num">4</div>
            <div className="spxl-pipe-title">You Get a Confidence Level</div>
            <div className="spxl-pipe-desc">
              When all three agree on the best trade — HIGH confidence.
              When they disagree — MEDIUM or LOW, so you know to look
              closer before acting.
            </div>
          </div>
        </div>
      </section>

      {/* ---- Features ---- */}
      <section className="spxl-section">
        <div className="spxl-section-label">What You Get</div>
        <h2 className="spxl-section-title">Everything You Need to Trade SPX Puts</h2>
        <p className="spxl-section-desc">
          No spreadsheets. No guessing. You get clear recommendations with
          plain-English explanations of why each trade was picked — so you
          always know what you're looking at.
        </p>

        <div className="spxl-features">
          {[
            { icon: 'TOP 3', title: 'Three Best Picks', desc: 'See the #1 trade from our model, Claude, and ChatGPT side by side. Same format, same metrics — so you can compare instantly and pick with confidence.' },
            { icon: 'DTE', title: 'Grouped by Time Horizon', desc: 'Trades expiring this week compete against each other, not against monthly trades. Fair comparisons mean better picks, whether you want quick wins or longer setups.' },
            { icon: 'AI', title: 'AI Explains Its Thinking', desc: 'Not just a number — the AI tells you why it chose each trade. Weekend risk? Low volume? Unusual skew? You see the reasoning in plain English.' },
            { icon: 'W/E', title: 'Weekend Risk Built In', desc: 'Options held over the weekend carry extra risk from news events. Our model automatically adjusts for this — so weekend-crossing trades don\'t sneak to the top unfairly.' },
            { icon: 'DOC', title: 'Download Full Reports', desc: 'Get a Word document with everything — rankings, AI picks, reasoning, and market context. Save it, print it, or share it with your trading group.' },
            { icon: 'HX', title: 'Check Past Picks', desc: 'Browse any past day to see what was recommended and how it played out. Full transparency — nothing is hidden or deleted.' },
          ].map((f) => (
            <div className="spxl-feat" key={f.title}>
              <div className="spxl-feat-icon" style={{ background: 'rgba(139,92,246,0.1)', color: '#a78bfa', borderRadius: '8px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.02em', border: '1px solid rgba(139,92,246,0.2)' }}>{f.icon}</div>
              <div className="spxl-feat-title">{f.title}</div>
              <div className="spxl-feat-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Tri-Validation ---- */}
      <section className="spxl-section">
        <div className="spxl-section-label">Triple Validation</div>
        <h2 className="spxl-section-title">Three Independent Minds. One Recommendation.</h2>
        <p className="spxl-section-desc">
          Instead of relying on one source, we use three. Our math model crunches the numbers.
          Two world-class AIs review the same data independently. When all three point
          to the same trade — that's a read worth acting on.
        </p>

        <div className="spxl-tri-grid">
          {[
            { cls: 'spxl-tri-card-model', label: 'QM', name: 'Our Quantitative Model', desc: 'Our math engine scores every option on how much you could earn, how safe the trade is, and how likely it is to win. It compares options fairly within the same time window so no trade gets an unfair advantage.', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)', color: '#f59e0b' },
            { cls: 'spxl-tri-card-claude', label: 'CL', name: 'Claude AI (Anthropic)', desc: 'Anthropic\'s advanced AI reads the same raw market data and picks its own best trade — completely independent from our model. It explains its reasoning so you can see exactly why it chose each pick.', bg: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.3)', color: '#a78bfa' },
            { cls: 'spxl-tri-card-gpt', label: 'GP', name: 'ChatGPT (OpenAI)', desc: 'OpenAI\'s leading AI provides a second independent opinion on the same data. When all three agree on the best trade, you can act with much higher confidence than trusting any single source alone.', bg: 'rgba(20,184,166,0.1)', border: 'rgba(20,184,166,0.3)', color: '#2dd4bf' },
          ].map((t) => (
            <div className={`spxl-tri-card ${t.cls}`} key={t.name}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: t.bg, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem', fontSize: '0.85rem', fontWeight: 800, color: t.color }}>{t.label}</div>
              <div className="spxl-tri-name">{t.name}</div>
              <div className="spxl-tri-desc">{t.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Confidence Scoring ---- */}
      <section className="spxl-section">
        <div className="spxl-section-label">Confidence Framework</div>
        <h2 className="spxl-section-title">Know How Much to Trust Each Pick</h2>
        <p className="spxl-section-desc">
          Every pick gets a simple confidence rating. HIGH means all three sources agree.
          MEDIUM means they mostly agree. LOW means they disagree — and you should
          look more carefully before trading.
        </p>

        <table className="spxl-conf-table">
          <thead>
            <tr>
              <th>Agreement</th>
              <th>Confidence</th>
              <th>What It Means</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>All three pick the same #1 trade</td>
              <td><span className="spxl-badge spxl-badge-high">HIGH</span></td>
              <td>Strong agreement — math and both AIs converge on the same answer</td>
            </tr>
            <tr>
              <td>Rankings are close but not identical</td>
              <td><span className="spxl-badge spxl-badge-med">MEDIUM</span></td>
              <td>Mostly agree — small differences in how they weigh the factors</td>
            </tr>
            <tr>
              <td>Rankings differ significantly</td>
              <td><span className="spxl-badge spxl-badge-low">LOW</span></td>
              <td>Disagreement — take a closer look before trading this one</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* ---- Why Not ChatGPT ---- */}
      <section className="spxl-section" style={{ background: 'rgba(139,92,246,0.03)', borderTop: '1px solid rgba(139,92,246,0.1)', borderBottom: '1px solid rgba(139,92,246,0.1)' }}>
        <div className="spxl-section-label">Why Us, Not ChatGPT</div>
        <h2 className="spxl-section-title">A Quantitative Engine, Not a Chatbot</h2>
        <p className="spxl-section-desc">
          ChatGPT cannot see live option chains, evaluate real bid/ask spreads, or manage trading risk.
          Our system does all of this automatically, every snapshot.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem', maxWidth: '800px', margin: '0 auto' }}>
          {[
            { label: 'Live Market Data', us: 'Real bid/ask from TastyTrade WebSocket', them: 'No live data access' },
            { label: 'Option Chain Analysis', us: '700+ puts scored per snapshot', them: 'Cannot read option chains' },
            { label: 'Risk Management', us: 'Min credit, safety cushion, DTE weighting', them: 'No risk parameters' },
            { label: 'Transparency', us: 'Downloadable reports with full rationale', them: 'Text response, no audit trail' },
          ].map((row) => (
            <div key={row.label} style={{ padding: '1.25rem', borderRadius: '10px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem', color: '#e2e8f0' }}>{row.label}</div>
              <div style={{ fontSize: '0.8rem', color: '#a78bfa', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600 }}>Us:</span> {row.us}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600 }}>ChatGPT:</span> {row.them}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Pricing ---- */}
      <section className="spxl-section">
        <div className="spxl-section-label">Pricing</div>
        <h2 className="spxl-section-title">SPX Signals — $79/month</h2>
        <p className="spxl-section-desc">
          Professional-grade quantitative research powered by live market data.
          60-day money-back guarantee. Cancel anytime.
        </p>
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <Link to="/login?redirect=upgrade" className="spxl-btn spxl-btn-primary spxl-btn-lg">
            Subscribe to SPX Signals
          </Link>
          <p style={{ marginTop: '1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
            Want everything? Get the <Link to="/login?redirect=upgrade" style={{ color: '#ec4899' }}>All Signals Bundle</Link> for $199/mo and save $57/mo.
          </p>
        </div>
      </section>

      {/* ---- CTA ---- */}
      <section className="spxl-cta">
        <h2>Ready to Trade Smarter?</h2>
        <p>
          Join traders who rely on triple-validated research — not gut feelings —
          to sell SPX puts with confidence.
        </p>
        <div className="spxl-hero-actions">
          <Link to="/login?redirect=upgrade" className="spxl-btn spxl-btn-primary spxl-btn-lg">
            Subscribe Now
          </Link>
          <Link to="/login" className="spxl-btn spxl-btn-outline spxl-btn-lg">
            Sign In
          </Link>
        </div>
      </section>

      {/* ---- Footer ---- */}
      <footer className="spxl-footer">
        SPX Signals is designed for educational and analytical purposes. All trading involves risk.
        Past research performance does not guarantee future results.
      </footer>
    </div>
  );
};
